"use client"
import Link from "../Link"
import Logo from "../icons/Logo"
import MenuIcon from "../icons/MenuIcon"
import SearchIcon from "../icons/SearchIcon"
import NavbarLinks from "./NavbarLinks"
import { useContext, useEffect, useState } from "react"
import { screenSizeContext } from "@/app/store/context/screenSizesContext"
import CustomButton from "../CustomButton"
import dynamic from "next/dynamic"
import { useQuery } from "@tanstack/react-query"
import { generalGetRequest } from "@/app/config/APIConfig"
import { useNavigation } from "@/app/hooks/useNavigation"
import { usePathname } from "next/navigation"
import SocialMedia from "../Footer/SocialMedia"
import { useGetInTouchPopupContext } from "@/app/store/context/getInTouchPopupContext"
import * as gtag from "@/app/utils/gtag"

const MainMenu = dynamic(() => import('./MainMenu'), { ssr: false });
const SearchPopup = dynamic(() => import('../search/SearchPopup'), { ssr: false });

const Navbar = () => {
	const { openGetInTouchPopup } = useGetInTouchPopupContext();

	const handleBuyNowClick = () => {
		gtag.conversion('s22iCL--jN8bELy50MNC');
		openGetInTouchPopup();
	};
	const pathname = usePathname()
	const router = useNavigation()
	const [isMenuOpened, setIsMenuOpened] = useState(false)
	const [isSearchPopupOpened, setIsSearchPopupOpened] = useState(false)
	const [hideNavbarBuyNow, setHideNavbarBuyNow] = useState(false)
	const [destinations, setDestinations] = useState([])
	const { isIpad, isMobile, isReady } = useContext(screenSizeContext);
	const menuLinks = [
		{ id: 1, title: "About Us", link: "/about-us" },
		{ id: 2, title: "Neighborhoods", link: "/neighborhoods" },
		{ id: 3, title: "Amenities ", link: "/amenities" },
		{ id: 4, title: "Contact Us", link: "/contact-us" },
		// { id: 5, title: "Click to Request", link: "/request" },
	]
	const toggleMenu = () => {
		setIsMenuOpened(!isMenuOpened)
	}
	const toggleSearchPopup = () => {
		if (isIpad) {
			if (isSearchPopupOpened) {
				if(isIpad) document.documentElement.style.overflow = "auto";
				else window.lenis?.startScroll?.()
			} else {
				if(isIpad) document.documentElement.style.overflow = "hidden";
				else window.lenis?.stopScroll?.()
			}
		}
		setIsSearchPopupOpened(!isSearchPopupOpened)

	}

	const { data, isLoading } = useQuery({
		queryKey: ["destinations"],
		queryFn: () => generalGetRequest("api/destinations"),
	});

	const { data: destinationsResp, isSuccess } = useQuery({
		queryKey: ["cities"],
		queryFn: () => generalGetRequest("api/cities"),
	});

	useEffect(() => {
		// GSAP/ScrollTrigger access DOM APIs; import them only in the browser.
		if (typeof window === "undefined" || !isReady) return;

		let out = false
		let dirBack = 0
		let navbarTrigger: any
		let sticky: any
		let cancelled = false

		void (async () => {
			const gsapPkg: any = await import("gsap")
			const scrollTriggerPkg: any = await import("gsap/ScrollTrigger")

			const gsap = gsapPkg?.gsap ?? gsapPkg?.default ?? gsapPkg
			const ScrollTrigger = scrollTriggerPkg?.ScrollTrigger ?? scrollTriggerPkg
			gsap?.registerPlugin?.(ScrollTrigger)

			if (cancelled) return

			navbarTrigger = ScrollTrigger.create({
				trigger: ".navbar_section",
				start: "top top",
				end: "bottom top",
				onLeave: () => {
					gsap.to(".navbar_section", { y: "-105%" })
					out = true
				},
				onEnterBack: () => {
					gsap.to(".navbar_section", { y: "0", background: "transparent", backdropFilter: "blur(0px)" })
					out = false
				}
			})

			sticky = ScrollTrigger.create({
				trigger: isIpad ? ".page_wrapper" : document.body,
				start: "top top",
				end: "bottom bottom",
				pin: ".navbar_section",
				pinSpacing: false,
				onUpdate: ({ direction }: { direction: number }) => {
					if (window.lenis?.isStopped) {
						gsap.to(".navbar_section", { y: "-105%" })
					} else {
						if (out) {
							if (direction === 1) {
								dirBack = 1
								gsap.to(".navbar_section", { y: "-105%" })
							} else if (direction === -1 && dirBack != -1) {
								dirBack = -1
								gsap.to(".navbar_section", { y: "0", background: "rgba(9, 26, 50, 0.50)", backdropFilter: "blur(45px)" })
							}
						}
					}
				}
			})
		})()

		return () => {
			cancelled = true
			navbarTrigger?.kill?.()
			sticky?.kill?.()
		}
	}, [pathname, isIpad, isReady])

	useEffect(() => {
		if (isSuccess) {
			setDestinations(destinationsResp.data.data.cities[0].destinations)
		}
	}, [isSuccess, destinationsResp])

	useEffect(() => {
		const isNeighborhoodDetail = /^\/neighborhoods\/[^/]+$/.test(pathname || "")
		if (!isNeighborhoodDetail) {
			setHideNavbarBuyNow(false)
			return
		}

		const handleStickyTabsChange = (event: Event) => {
			const { isSticky } = (event as CustomEvent<{ isSticky: boolean }>).detail
			setHideNavbarBuyNow(isSticky)
		}

		window.addEventListener("sticky-tabs-change", handleStickyTabsChange)
		return () => {
			window.removeEventListener("sticky-tabs-change", handleStickyTabsChange)
			setHideNavbarBuyNow(false)
		}
	}, [pathname])
	//social media
	const socialMedia = [
		{
			link: "https://www.facebook.com/OWest.6oct/",
			label: "O West on Facebook",
			icon: (
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g clipPath="url(#clip0_3996_4804)">
						<path
							d="M12 0.000488281C5.37264 0.000488281 0 5.37313 0 12.0005C0 17.628 3.87456 22.3502 9.10128 23.6472V15.6677H6.62688V12.0005H9.10128V10.4203C9.10128 6.33601 10.9498 4.44289 14.9597 4.44289C15.72 4.44289 17.0318 4.59217 17.5685 4.74097V8.06497C17.2853 8.03521 16.7933 8.02033 16.1822 8.02033C14.2147 8.02033 13.4544 8.76577 13.4544 10.7035V12.0005H17.3741L16.7006 15.6677H13.4544V23.9126C19.3963 23.195 24.0005 18.1358 24.0005 12.0005C24 5.37313 18.6274 0.000488281 12 0.000488281Z"
							fill="white"
						/>
					</g>
					<defs>
						<clipPath id="clip0_3996_4804">
							<rect
								width="24"
								height="24"
								fill="white"
								transform="translate(0 0.000488281)"
							/>
						</clipPath>
					</defs>
				</svg>
			),
		},
		{
			link: "https://www.instagram.com/owest.6oct?igsh=MTl3M2h2bjF4amcxMA==",
			label: "O West on Instagram",
			icon: (
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g clipPath="url(#clip0_3996_4806)">
						<path
							d="M12 2.16143C15.2063 2.16143 15.5859 2.17549 16.8469 2.23174C18.0188 2.2833 18.6516 2.48018 19.0734 2.64424C19.6313 2.85986 20.0344 3.12236 20.4516 3.53955C20.8734 3.96143 21.1313 4.35986 21.3469 4.91768C21.5109 5.33955 21.7078 5.97705 21.7594 7.14424C21.8156 8.40986 21.8297 8.78955 21.8297 11.9911C21.8297 15.1974 21.8156 15.5771 21.7594 16.838C21.7078 18.0099 21.5109 18.6427 21.3469 19.0646C21.1313 19.6224 20.8687 20.0255 20.4516 20.4427C20.0297 20.8646 19.6313 21.1224 19.0734 21.338C18.6516 21.5021 18.0141 21.6989 16.8469 21.7505C15.5813 21.8067 15.2016 21.8208 12 21.8208C8.79375 21.8208 8.41406 21.8067 7.15313 21.7505C5.98125 21.6989 5.34844 21.5021 4.92656 21.338C4.36875 21.1224 3.96563 20.8599 3.54844 20.4427C3.12656 20.0208 2.86875 19.6224 2.65313 19.0646C2.48906 18.6427 2.29219 18.0052 2.24063 16.838C2.18438 15.5724 2.17031 15.1927 2.17031 11.9911C2.17031 8.78486 2.18438 8.40518 2.24063 7.14424C2.29219 5.97236 2.48906 5.33955 2.65313 4.91768C2.86875 4.35986 3.13125 3.95674 3.54844 3.53955C3.97031 3.11768 4.36875 2.85986 4.92656 2.64424C5.34844 2.48018 5.98594 2.2833 7.15313 2.23174C8.41406 2.17549 8.79375 2.16143 12 2.16143ZM12 0.000488281C8.74219 0.000488281 8.33438 0.0145508 7.05469 0.0708008C5.77969 0.127051 4.90313 0.333301 4.14375 0.628613C3.35156 0.937988 2.68125 1.3458 2.01563 2.01611C1.34531 2.68174 0.9375 3.35205 0.628125 4.13955C0.332812 4.90361 0.126563 5.77549 0.0703125 7.05049C0.0140625 8.33486 0 8.74268 0 12.0005C0 15.2583 0.0140625 15.6661 0.0703125 16.9458C0.126563 18.2208 0.332812 19.0974 0.628125 19.8567C0.9375 20.6489 1.34531 21.3192 2.01563 21.9849C2.68125 22.6505 3.35156 23.063 4.13906 23.3677C4.90313 23.663 5.775 23.8692 7.05 23.9255C8.32969 23.9817 8.7375 23.9958 11.9953 23.9958C15.2531 23.9958 15.6609 23.9817 16.9406 23.9255C18.2156 23.8692 19.0922 23.663 19.8516 23.3677C20.6391 23.063 21.3094 22.6505 21.975 21.9849C22.6406 21.3192 23.0531 20.6489 23.3578 19.8614C23.6531 19.0974 23.8594 18.2255 23.9156 16.9505C23.9719 15.6708 23.9859 15.263 23.9859 12.0052C23.9859 8.74736 23.9719 8.33955 23.9156 7.05986C23.8594 5.78486 23.6531 4.9083 23.3578 4.14893C23.0625 3.35205 22.6547 2.68174 21.9844 2.01611C21.3188 1.35049 20.6484 0.937988 19.8609 0.633301C19.0969 0.337988 18.225 0.131738 16.95 0.0754883C15.6656 0.0145508 15.2578 0.000488281 12 0.000488281Z"
							fill="white"
						/>
						<path
							d="M12 5.83643C8.59688 5.83643 5.83594 8.59736 5.83594 12.0005C5.83594 15.4036 8.59688 18.1646 12 18.1646C15.4031 18.1646 18.1641 15.4036 18.1641 12.0005C18.1641 8.59736 15.4031 5.83643 12 5.83643ZM12 15.9989C9.79219 15.9989 8.00156 14.2083 8.00156 12.0005C8.00156 9.79268 9.79219 8.00205 12 8.00205C14.2078 8.00205 15.9984 9.79268 15.9984 12.0005C15.9984 14.2083 14.2078 15.9989 12 15.9989Z"
							fill="white"
						/>
						<path
							d="M19.8469 5.59263C19.8469 6.3895 19.2 7.03169 18.4078 7.03169C17.6109 7.03169 16.9688 6.38482 16.9688 5.59263C16.9688 4.79575 17.6156 4.15356 18.4078 4.15356C19.2 4.15356 19.8469 4.80044 19.8469 5.59263Z"
							fill="white"
						/>
					</g>
					<defs>
						<clipPath id="clip0_3996_4806">
							<rect
								width="24"
								height="24"
								fill="white"
								transform="translate(0 0.000488281)"
							/>
						</clipPath>
					</defs>
				</svg>
			),
		},
		{
			link: "https://www.linkedin.com/company/owestorascom/",
			label: "O West on LinkedIn",
			icon: (
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g clipPath="url(#clip0_3996_4810)">
						<path
							d="M22.2234 0.000488281H1.77187C0.792187 0.000488281 0 0.773926 0 1.73018V22.2661C0 23.2224 0.792187 24.0005 1.77187 24.0005H22.2234C23.2031 24.0005 24 23.2224 24 22.2708V1.73018C24 0.773926 23.2031 0.000488281 22.2234 0.000488281ZM7.12031 20.452H3.55781V8.9958H7.12031V20.452ZM5.33906 7.43486C4.19531 7.43486 3.27188 6.51143 3.27188 5.37236C3.27188 4.2333 4.19531 3.30986 5.33906 3.30986C6.47813 3.30986 7.40156 4.2333 7.40156 5.37236C7.40156 6.50674 6.47813 7.43486 5.33906 7.43486ZM20.4516 20.452H16.8937V14.8833C16.8937 13.5567 16.8703 11.8458 15.0422 11.8458C13.1906 11.8458 12.9094 13.2942 12.9094 14.7896V20.452H9.35625V8.9958H12.7687V10.5614H12.8156C13.2891 9.66143 14.4516 8.70986 16.1813 8.70986C19.7859 8.70986 20.4516 11.0817 20.4516 14.1661V20.452Z"
							fill="white"
						/>
					</g>
					<defs>
						<clipPath id="clip0_3996_4810">
							<rect
								width="24"
								height="24"
								fill="white"
								transform="translate(0 0.000488281)"
							/>
						</clipPath>
					</defs>
				</svg>
			),
		},
	];
	return (
		<>
		
			<div className="navbar_section border-b border-oWhite-20 absolute top-0 w-full z-[997]">
				{/* <TopBanner /> */}
				<div className="flex items-center justify-between flex-nowrap px h-20">
					<div className="navbar_section_logo flex items-center h-full lg:border-r lg:border-oWhite-20 pr-12 small-mobile:pr-8">
						<Link className="flex align_center" href={"/"} aria-label="O West home">
							{isMobile ? <Logo width={216} height={48} customClass="small-mobile:!w-[186px]" /> : <Logo width={274} height={48} />}
						</Link>
					</div>
					{!isIpad && !isMobile && (
						<NavbarLinks data={menuLinks} />
					)}
					{!isIpad && !isMobile ? (
						<div className="flex items-center gap-4 ">
							<div className="social-media flex-row  mb-16 lg:mb-0">
								<SocialMedia data={socialMedia} />
							</div>
							{!hideNavbarBuyNow && (
								<div
									className=" px-4 h-10 flex items-center justify-center  text-oWhite-100 rounded-full"
								>
									<CustomButton text="Buy now" customClass="navbar_icons_button" hasIcon action={handleBuyNowClick} />
								</div>
							)}
							<div className="navbar_icons_search cursor-pointer" onClick={toggleSearchPopup}>
								<SearchIcon />
							</div>
							<div className="navbar_icons_bar cursor-pointer" onClick={() => {
								setIsMenuOpened(true);
							}}>
								<MenuIcon color="white" />
							</div>
						</div>
					) : (
						<div className="flex items-center gap-4  h-full  border-l  md:pl-11 pl-5  border-oWhite-20">
							<div className="navbar_icons_search cursor-pointer" onClick={toggleSearchPopup}>
								<SearchIcon />
							</div>
							<div className="navbar_icons_bar cursor-pointer " onClick={() => {
								setIsMenuOpened(true);
								// if(isIpad) document.documentElement.style.overflow = "hidden";
								// document.body.classList.add("no-scroll")
							}}>
								<MenuIcon color="white" />
							</div>
						</div>
					)}
				</div>
			</div>
			<MainMenu
				isMenuOpen={isMenuOpened}
				// toggleMenu={toggleMenu}
				destinations={data?.data?.data?.data?.destinations}
				setIsMenuOpened={setIsMenuOpened}
				onBuyNowClick={handleBuyNowClick}
			/>
			<SearchPopup
				isSearchPopupOpen={isSearchPopupOpened}
				toggleSearchPopup={toggleSearchPopup}
				destinations={destinations}
			/>
		</>
	)
}

export default Navbar